import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, ChevronLeft, ChevronRight } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { notificationAPI } from '../services/api';
import { useNotifications } from '../context/NotificationContext';

const formatTimeAgo = (dateString) => {
    const now = new Date();
    const then = new Date(dateString);
    const h = Math.floor((now - then) / (1000 * 60 * 60));
    if (h < 1) return 'Just now';
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d ago`;
    const w = Math.floor(d / 7);
    if (w < 4) return `${w}w ago`;
    return `${Math.floor(d / 30)}mo ago`;
};

const Notifications = () => {
    const navigate = useNavigate();
    const { refreshUnreadCount } = useNotifications();
    const [notifications, setNotifications] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, pages: 1 });
    const [loading, setLoading] = useState(true);

    const fetchPage = async (page = 1) => {
        try {
            setLoading(true);
            const res = await notificationAPI.getAll(page, 20);
            if (res.success) {
                setNotifications(res.data);
                setPagination(res.pagination);
            }
        } catch (error) {
            console.error('Error loading notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPage(1);
    }, []);

    const handleClick = async (notification) => {
        if (!notification.is_read) {
            setNotifications(prev =>
                prev.map(n => (n.id === notification.id ? { ...n, is_read: true } : n))
            );
            try {
                await notificationAPI.markAsRead(notification.id);
                refreshUnreadCount();
            } catch (error) {
                console.error('Error marking notification as read:', error);
            }
        }
        if (notification.link) {
            navigate(notification.link);
        }
    };

    const handleMarkAllRead = async () => {
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        try {
            await notificationAPI.markAllAsRead();
            refreshUnreadCount();
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
    };

    const hasUnread = notifications.some(n => !n.is_read);

    return (
        <div className="min-h-screen bg-gray-950">
            <PageHeader title="Notifications" subtitle="Stay up to date with your activity" />

            <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
                <div className="flex items-center justify-end mb-4">
                    {hasUnread && (
                        <button
                            onClick={handleMarkAllRead}
                            className="text-sm text-blue-400 hover:text-blue-300"
                        >
                            Mark all as read
                        </button>
                    )}
                </div>

                {loading ? (
                    <div className="space-y-3">
                        {[...Array(5)].map((_, i) => (
                            <div key={i} className="glass-card p-4 h-20 animate-pulse" />
                        ))}
                    </div>
                ) : notifications.length === 0 ? (
                    <div className="glass-card p-10 text-center">
                        <Bell className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                        <p className="text-gray-400">No notifications yet</p>
                    </div>
                ) : (
                    <div className="glass-card divide-y divide-gray-700/50">
                        {notifications.map((n) => (
                            <button
                                key={n.id}
                                onClick={() => handleClick(n)}
                                className={`w-full text-left flex items-start gap-3 px-5 py-4 hover:bg-white/5 transition-colors ${!n.is_read ? 'bg-green-500/5' : ''}`}
                            >
                                {!n.is_read && (
                                    <span className="w-2 h-2 mt-1.5 bg-green-500 rounded-full flex-shrink-0"></span>
                                )}
                                <div className={n.is_read ? 'pl-5' : ''}>
                                    <p className="text-sm font-medium text-white">{n.title}</p>
                                    <p className="text-sm text-gray-400">{n.message}</p>
                                    <p className="text-xs text-gray-500 mt-1">{formatTimeAgo(n.created_at)}</p>
                                </div>
                            </button>
                        ))}
                    </div>
                )}

                {pagination.pages > 1 && (
                    <div className="flex items-center justify-center gap-4 mt-6">
                        <button
                            onClick={() => fetchPage(pagination.page - 1)}
                            disabled={pagination.page <= 1}
                            className="p-2 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <span className="text-sm text-gray-400">
                            Page {pagination.page} of {pagination.pages}
                        </span>
                        <button
                            onClick={() => fetchPage(pagination.page + 1)}
                            disabled={pagination.page >= pagination.pages}
                            className="p-2 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Notifications;
